/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react'
import { useBlockUI, useHttpClient } from '../../../../modules/services/Bundle'
import { EnumUsuarioTipo, RequestMethod } from '../../../../modules/services/core/_enums'
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import { defaultValidationSchemas, initialValues } from './helpers/UsuarioHelper'
import Select from 'react-select'
import { IPerfilModel } from '../../../../modules/services/core/_models'

// Função utilitária para aplicar máscara de celular
const applyPhoneMask = (value: string): string => {
  // Remove todos os caracteres não numéricos
  const numbers = value.replace(/\D/g, '');

  // Aplica a máscara baseada no tamanho
  if (numbers.length <= 2) {
    return numbers.replace(/(\d{0,2})/, '($1');
  } else if (numbers.length <= 7) {
    return numbers.replace(/(\d{2})(\d{0,5})/, '($1) $2');
  } else if (numbers.length <= 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
  }

  // Limita a 11 dígitos
  return numbers.substring(0, 11).replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

interface Dados {
  idUsuario: string
  tipo: number
  visualizar: boolean
  title: string
}

const Usuario = () => {
  const httpClient = useHttpClient()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as Dados
  const informacao: Dados = state
  const { setBlocking } = useBlockUI()
  const [perfis, setPerfis] = useState<IPerfilModel[]>([])
  const [organizacoes, setOrganizacoes] = useState<{
    OrganizationId: number;
    OrganizationName: string;
    Idcorporacao: number;
    CorporationName: string;
  }[]>([]);

  const formik = useFormik({
    initialValues: { ...initialValues },
    validationSchema: defaultValidationSchemas,
    onSubmit: async (values) => {

      values.Celular = values.Celular.replace(/\D/g, '');

      await httpClient.request({
        method: (informacao && informacao.idUsuario) ? RequestMethod.PUT : RequestMethod.POST,
        endpoint: `/Administracao/Usuario/${(informacao && informacao.idUsuario) ? "Editar" : "Adicionar"}`,
        data: values,
        ifSuccessRedirectTo: '/administracao/usuarios/lista'
      })
    },
  })

  const obterUsuario = async () => {
    try {
      console.log('Iniciando obterUsuario...');
      const response = await httpClient.request({
        method: RequestMethod.GET,
        endpoint: '/Administracao/Usuario/Obter',
        blockFree: true,
        queryObject: {
          tipo: informacao.tipo,
          idUsuario: informacao.idUsuario
        }
      })

      console.log('Resposta completa de obterUsuario:', JSON.stringify(response, null, 2));

      if (response.success && response.payload) {
        const emailUser = response.payload.Email;
        console.log('Email completo do usuário:', emailUser);

        // Atualizar os dados do formulário primeiro
        if (response.payload.Tipo.toString() === EnumUsuarioTipo['Microsoft Interno'].toString()) {
          response.payload.Email = response.payload.Email.split('@')[0];
          response.payload.Senha = '@Ab12345678';
        }
        formik.setValues(response.payload);

        // Depois buscar dados adicionais de forma assíncrona
        await buscarDadosAdicionais(emailUser);
      }
    } catch (error) {
      console.error("Erro ao obter usuário:", error);
    }
  }

  const obterIdUsuario = async (email: string) => {
    try {
      console.log('Email original:', email);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/User/ByEmail/${email}`;
      console.log('URL completa:', url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "*/*",
        },
      });

      const responseJson = await response.json();
      console.log("Resposta da API:", responseJson);

      // Se a resposta for diretamente um número
      if (typeof responseJson === 'number') {
        return responseJson;
      }
      // Se a resposta for um objeto com propriedade id
      else if (responseJson && responseJson.id) {
        return responseJson.id;
      } else {
        console.error("ID do usuário não encontrado na resposta:", responseJson);
        return null;
      }

    } catch (error) {
      console.error("Erro detalhado ao buscar ID do usuário:", error);
      return null;
    }
  };

  const buscarDadosAdicionais = async (email: string) => {
    try {
      console.log('Buscando dados adicionais para:', email);
      const userId = await obterIdUsuario(email);
      console.log('ID do usuário obtido:', userId);

      if (userId && userId > 0) {
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/UserOrganization/CorpByUser?userId=${userId}`;
          console.log('URL da requisição de corporações:', url);

          const corpResponse = await fetch(url, {
            method: "GET",
            headers: {
              accept: "*/*",
            },
          });

          if (!corpResponse.ok) {
            throw new Error(`Erro na resposta: ${corpResponse.status} ${corpResponse.statusText}`);
          }

          const corpData = await corpResponse.json();
          console.log('Dados das corporações:', corpData);
          setOrganizacoes(corpData);
        } catch (error) {
          console.error("Erro ao buscar corporações:", error);
        }
      } else {
        console.warn('Não foi possível buscar corporações pois o ID do usuário não foi obtido ou é inválido (ID:', userId, ')');
      }
    } catch (error) {
      console.error("Erro ao buscar dados adicionais:", error);
    }
  }

  // Função para agrupar organizações por corporação
  const organizacoesPorCorporacao = useMemo(() => {
    const grupos = organizacoes.reduce((acc, org) => {
      const corp = acc.find(c => c.Idcorporacao === org.Idcorporacao);
      if (corp) {
        corp.organizacoes.push(org);
      } else {
        acc.push({
          Idcorporacao: org.Idcorporacao,
          CorporationName: org.CorporationName,
          organizacoes: [org]
        });
      }
      return acc;
    }, [] as Array<{
      Idcorporacao: number;
      CorporationName: string;
      organizacoes: typeof organizacoes;
    }>);

    return grupos;
  }, [organizacoes]);

  const obterPerfis = async () => {

    setPerfis([])

    await httpClient.request({
      method: RequestMethod.GET,
      endpoint: '/Administracao/Perfil/ObterParaSelect',
      blockFree: true
    }).then((response) => {
      if (response.success && response.payload) setPerfis(response.payload)
    })

  }

  const obterDados = async () => {
    try {
      console.log('Iniciando obterDados...');
      setBlocking(true);

      const promises = [];

      if (informacao && informacao.idUsuario && informacao.tipo) {
        promises.push(obterUsuario());
      }

      promises.push(obterPerfis());

      await Promise.all(promises);

    } catch (error) {
      console.error("Erro ao obter dados:", error);
    } finally {
      console.log('Finalizando obterDados...');
      console.log()
      setBlocking(false);
    }
  }

  useEffect(() => {
    if (!informacao || !informacao.idUsuario) {
      formik.setFieldValue("Perfis", [])
    }
  }, [formik.values.Tipo])

  useEffect(() => {
    obterDados()
  }, [informacao])

  return (
    <>
      <div className='card mb-5 mb-xl-10'>

        <div className='card-header'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>{informacao.title} Usuário</h3>
          </div>
        </div>

        <div className='card-body'>
          <FormikProvider value={formik}>
            <div className='w-100'>
              <form noValidate >

                <div className="row">
                  <div className='col-lg-6 fv-row mb-5'>
                    <label className='form-label required'>Tipo</label>
                    <Field
                      as='select'
                      name='Tipo'
                      className='form-select form-select-lg form-select-solid'
                      value={formik.values.Tipo}
                      disabled={informacao && informacao.idUsuario}
                    >
                      {Object.keys(EnumUsuarioTipo).map(key => (

                        parseInt(key) > 0 && (
                          <option key={key} value={parseInt(key)}>
                            {EnumUsuarioTipo[parseInt(key)]}
                          </option>
                        )

                      ))}
                    </Field>
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='Tipo' />
                    </div>
                  </div>
                  <div className='col-lg-6 fv-row mb-5'>
                    <label className='form-label required'>Nome</label>
                    <Field
                      name='Nome'
                      placeholder='Nome do usuário'
                      className='form-control form-control-lg form-control-solid'
                      value={formik.values.Nome}
                      disabled={informacao && informacao.idUsuario && informacao.visualizar}
                    />
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='Nome' />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className='col-lg-6 fv-row mb-5'>
                    <label className='form-label required'>Email</label>
                    <div className={formik.values.Tipo.toString() === EnumUsuarioTipo['Microsoft Interno'].toString() ? 'input-suffix-taggen' : ''}>
                      <Field
                        name='Email'
                        placeholder='Email do usuário'
                        className='form-control form-control-lg form-control-solid'
                        value={formik.values.Email}
                        disabled={((informacao && informacao.idUsuario && informacao.visualizar) || formik.values.Tipo === EnumUsuarioTipo['Microsoft Externo'] || formik.values.Tipo === EnumUsuarioTipo['Domínio Externo'])}
                      />
                    </div>
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='Email' />
                    </div>
                  </div>
                  {(!informacao || !informacao.idUsuario) && (
                    <div className='col-lg-6 fv-row mb-5'>
                      <label className='form-label required'>Senha</label>
                      <Field
                        name='Senha'
                        placeholder='Senha do usuário'
                        className='form-control form-control-lg form-control-solid'
                        value={formik.values.Senha}
                      />
                      <div className='text-danger mt-2'>
                        <ErrorMessage name='Senha' />
                      </div>
                    </div>
                  )}
                  <div className='col-lg-6 fv-row mb-5'>
                    <label className='form-label'>Perfis (Permissões)</label>
                    <Select
                      placeholder='Selecione...'
                      className='react-select-styled react-select-lg react-select-solid'
                      classNamePrefix='react-select'
                      noOptionsMessage={() => 'Nenhum registro encontrado'}
                      {...formik.getFieldProps('Perfis')}
                      options={perfis.map((data) => ({
                        value: data.Id,
                        label: data.Nome
                      }))}
                      isDisabled={informacao && informacao.visualizar}
                      value={perfis && perfis.length > 0 && formik.values.Perfis && formik.values.Perfis.length > 0 ? formik.values.Perfis.map((idPerfil) => ({
                        value: idPerfil,
                        label: perfis.find(x => x.Id === idPerfil)?.Nome ?? ''
                      })) : []}
                      isSearchable
                      isMulti
                      onChange={(e: any) => formik.setFieldValue("Perfis", e.map((data: any) => { return data.value }))}
                    />
                    <div className='text-danger mt-2'>
                      <ErrorMessage name='Perfis' />
                    </div>
                  </div>
                </div>
                {organizacoesPorCorporacao.length > 0 && (
                  <div className="row mt-5">
                    <div className="col-12">
                      <h4 className="mb-4">Corporações e Organizações</h4>
                      <div className="list-group">
                        {organizacoesPorCorporacao.map((corp) => (
                          <div key={corp.Idcorporacao} className="list-group-item">
                            <div className="d-flex align-items-center mb-2">
                              <i className="bi bi-building me-2"></i>
                              <h5 className="mb-0">{corp.CorporationName}</h5>
                            </div>
                            <div className="ms-4">
                              {corp.organizacoes.map((org) => (
                                <div key={org.OrganizationId} className="d-flex align-items-center py-1">
                                  <i className="bi bi-diagram-3 me-2"></i>
                                  <span>{org.OrganizationName}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              </form>
            </div>
          </FormikProvider>
        </div>
        <div className="card-footer py-4 d-flex justify-content-between">
          <button
            type='button'
            className='btn btn-secondary'
            onClick={() => navigate(-1)}
          >
            Voltar
          </button>
          {informacao.title && informacao.title !== 'Visualizar' && (
            <button
              type='button'
              onClick={() => formik.handleSubmit()}
              className={`btn btn-lg btn-primary ${informacao && informacao.visualizar ? "d-none" : ""}`}
            >
              {informacao.title}
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export { Usuario }

